/**
 * app.tsx — the shell composition and the screen switch.
 *
 * `GET /api/summary` is fetched once per period and feeds the top bar, the banner, the bell and
 * the home screen (api-personal.md §3.2). The first load renders skeletons; a refresh keeps the
 * previous render at 50% with `aria-busy` (spec §2.4 / §3.5). Everything is gated on the
 * session: without one the router replaces the route with /login and remembers the intended
 * path in `?next=`.
 */

import { useEffect, useState } from 'react';
import type { SummaryResponse } from '../../shared-types.ts';
import { he } from './i18n/he.ts';
import { ApiError } from './api-select.ts';
import { BottomNav, ExpiredBanner, FreshRow, ScreenBody, Sidebar, Topbar } from './components/Shell.tsx';
import { ScreenSkeleton } from './components/ui.tsx';
import { AppProvider, useApp } from './hooks/useApp.tsx';
import { cacheKey } from './hooks/cache.ts';
import { useQuery } from './hooks/useQuery.ts';
import { useIsMobile } from './hooks/useMediaQuery.ts';
import { RouterProvider, useRouter } from './hooks/useRouter.tsx';
import { buildPath, settingsTab, type Route } from './router.ts';
import { CATEGORY } from './i18n/he.ts';
import { AlertsScreen } from './screens/Alerts.tsx';
import { CardsScreen } from './screens/Cards.tsx';
import { CategoriesScreen } from './screens/Categories.tsx';
import { HomeScreen } from './screens/Home.tsx';
import { LoansScreen } from './screens/Loans.tsx';
import { LoginScreen } from './screens/Login.tsx';
import { NetWorthScreen } from './screens/NetWorth.tsx';
import { SettingsScreen } from './screens/Settings.tsx';
import { TransactionsScreen } from './screens/Transactions.tsx';
import type { GroupKey } from '../../shared-types.ts';

export function App() {
  return (
    <RouterProvider>
      <AppShellBoot />
    </RouterProvider>
  );
}

function AppShellBoot() {
  const { navigate, route } = useRouter();
  return (
    <AppProvider
      onUnauthenticated={() => {
        if (route.screen !== 'login') navigate(buildPath('login', null, { next: route.path }), { replace: true });
      }}
    >
      <AppShell />
    </AppProvider>
  );
}

/** Screen title + (mobile) back link, per spec §3.1/§3.2. */
function screenMeta(route: Route, isMobile: boolean): { title: string; back?: string } {
  switch (route.screen) {
    case 'transactions':
      return { title: he.nav.transactions };
    case 'categories':
      return route.param && isMobile
        ? { title: CATEGORY[route.param as GroupKey]?.label ?? he.nav.categories, back: buildPath('categories') }
        : { title: he.nav.categories };
    case 'cards':
      return { title: he.nav.cards };
    case 'loans':
      return { title: he.nav.loans };
    case 'networth':
      return { title: he.nav.networth };
    case 'settings':
      return route.param && isMobile
        ? { title: he.settings.groups[settingsTab(route)] ?? he.settings.title, back: buildPath('settings') }
        : { title: he.settings.title };
    case 'alerts':
      return { title: he.alerts.title };
    case 'login':
      return { title: he.login.title };
    default:
      return { title: he.nav.home };
  }
}

function AppShell() {
  const { api, user, setUser, period, syncing } = useApp();
  const { route, navigate } = useRouter();
  const isMobile = useIsMobile();
  const [booted, setBooted] = useState(false);

  // restore the session on boot
  useEffect(() => {
    let cancelled = false;
    api.me().then(
      (response) => {
        if (cancelled) return;
        setUser(response.user);
        setBooted(true);
        if (route.screen === 'login') navigate(route.query.next || buildPath('home'), { replace: true });
      },
      (error: unknown) => {
        if (cancelled) return;
        setBooted(true);
        if (error instanceof ApiError && error.code !== 'UNAUTHENTICATED') return;
        if (route.screen !== 'login') navigate(buildPath('login', null, { next: route.path }), { replace: true });
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const summary = useQuery<SummaryResponse>(
    user ? cacheKey('summary', { period }) : null,
    () => api.summary(period),
    { scopes: ['summary'], enabled: Boolean(user) },
  );

  // reset the scroll position when the screen changes
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelector('.scrollarea')?.scrollTo(0, 0);
  }, [route.screen, route.param]);

  // the sticky top bar gets its hairline once the page scrolls
  useEffect(() => {
    const onScroll = () => {
      document.querySelector('.topbar')?.classList.toggle('scrolled', window.scrollY > 4);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.title = `${screenMeta(route, isMobile).title} · ${he.app.name}`;
  }, [route, isMobile]);

  if (route.screen === 'login') {
    return (
      <div className="shell login-mode">
        <div className="content">
          <main className="appmain" style={{ padding: 0, flex: '1 0 auto', display: 'flex', flexDirection: 'column' }}>
            <LoginScreen />
          </main>
        </div>
      </div>
    );
  }

  if (!booted || !user) {
    return (
      <div className="shell">
        <div className="content">
          <ScreenBody busy={false}>
            <ScreenSkeleton />
          </ScreenBody>
        </div>
      </div>
    );
  }

  const meta = screenMeta(route, isMobile);
  const busy = syncing || summary.busy;
  const firstLoad = summary.loading && !summary.data;

  return (
    <>
      <a className="skip" href="#main-content">
        {he.app.skip}
      </a>
      <div className="shell">
        <Sidebar summary={summary.data} />
        <div className="content">
          <Topbar summary={summary.data} title={meta.title} back={meta.back} />
          <FreshRow summary={summary.data} />
          <div className="scrollarea">
            <div className="bannerwrap" role="region" aria-label={he.banner.regionLabel}>
              <ExpiredBanner summary={summary.data} />
            </div>
            <ScreenBody busy={busy}>
              {firstLoad ? (
                <ScreenSkeleton />
              ) : (
                <Screen route={route} summary={summary.data} />
              )}
            </ScreenBody>
          </div>
          <BottomNav summary={summary.data} />
        </div>
      </div>
    </>
  );
}

function Screen({ route, summary }: { route: Route; summary: SummaryResponse | undefined }) {
  switch (route.screen) {
    case 'transactions':
      return <TransactionsScreen summary={summary} />;
    case 'categories':
      return <CategoriesScreen summary={summary} />;
    case 'cards':
      return <CardsScreen summary={summary} />;
    case 'loans':
      return <LoansScreen summary={summary} />;
    case 'networth':
      return <NetWorthScreen />;
    case 'settings':
      return <SettingsScreen summary={summary} />;
    case 'alerts':
      return <AlertsScreen summary={summary} />;
    default:
      return summary ? <HomeScreen summary={summary} /> : <ScreenSkeleton />;
  }
}
